import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, Sparkles, Loader2, Sun, Cloud, Leaf, Lightbulb, Layers } from 'lucide-react';
import { HudButton } from '../hud';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

/* Icons per category — kept tiny, only used in the segmented picker */
const CATEGORY_ICONS = {
  time_of_day: Sun,
  weather:     Cloud,
  season:      Leaf,
  lighting:    Lightbulb,
  material:    Layers,
};

/**
 * VariationDialog — modal that lets the project manager spawn a strict
 * variation of a *validated* parent render. Only one axis changes at a time.
 *
 * Props:
 *   - projectId   (str)
 *   - parentItem  ({job_id, view_type, lot_number, image_url, …})
 *   - onClose()
 *   - onCreated() — fired after a successful POST so the gallery refreshes
 */
const VariationDialog = ({ projectId, parentItem, onClose, onCreated }) => {
  const [options, setOptions] = useState(null);
  const [category, setCategory] = useState('time_of_day');
  const [value, setValue] = useState('');
  const [customNote, setCustomNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch the catalog from the backend so we never hard-code the lists in 2 places.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/generation/variation-options`, opts);
        if (cancelled) return;
        setOptions(r.data.categories || []);
      } catch (e) {
        toast.error('Impossible de charger les options de variation.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Re-init value when the category changes so we don't carry a stale key.
  const currentCat = useMemo(
    () => (options || []).find((c) => c.id === category),
    [options, category],
  );
  useEffect(() => {
    if (currentCat && currentCat.options?.length && !currentCat.options.some((o) => o.value === value)) {
      setValue(currentCat.options[0].value);
    }
  }, [currentCat, value]);

  // Esc closes the modal
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const needsNote = currentCat?.needs_note;
  const canSubmit = !!value && !submitting && (!needsNote || customNote.trim().length >= 3);

  const onSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/projects/${projectId}/generations/${parentItem.job_id}/variation`,
        { category, value, custom_note: customNote.trim() || null },
        opts,
      );
      toast.success('Variation lancée — apparaîtra dans la galerie sous le rendu parent.');
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec du lancement de la variation.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      data-testid="variation-dialog"
      style={{ background: 'rgba(6,8,12,0.86)', backdropFilter: 'blur(14px)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl overflow-hidden"
        style={{
          background: 'var(--c-surface-1)',
          border: '1px solid var(--c-border)',
          boxShadow: '0 30px 80px -20px rgba(0,0,0,0.85)',
          maxHeight: '92vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 pb-3" style={{ borderBottom: '1px solid var(--c-border)' }}>
          <div>
            <span className="hud-eyebrow hud-eyebrow-gold">Variation stricte</span>
            <h3
              className="mt-1 font-semibold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 20 }}
            >
              Générer une variation du rendu validé
            </h3>
            <p className="text-[12.5px] mt-1.5" style={{ color: 'var(--c-text-2)' }}>
              Tout est conservé à l'identique (caméra, mobilier, matériaux, layout).
              Un seul paramètre change.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="variation-dialog-close"
            className="rounded-lg p-2 transition-colors"
            style={{ background: 'transparent', color: 'var(--c-text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--c-gold)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--c-text-muted)'; }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto" style={{ maxHeight: 'calc(92vh - 170px)' }}>
          {/* Parent badge */}
          <div
            className="flex items-center gap-2 mb-4 rounded-lg p-2.5"
            style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
          >
            <span className="hud-badge" style={{
              background: 'rgba(34,197,94,0.18)', color: '#86EFAC',
              border: '1px solid rgba(34,197,94,0.35)', fontSize: 9.5, padding: '2px 7px',
            }}>
              ✓ Validé
            </span>
            <span className="text-[12px]" style={{ color: 'var(--c-text-2)' }}>
              Rendu parent · {parentItem.view_type === 'fps' ? 'Vue FPS' : 'Shoebox'}
              {parentItem.lot_number ? ` · Lot ${parentItem.lot_number}` : ''}
            </span>
          </div>

          {/* Category picker — segmented row */}
          <div className="mb-5">
            <label className="hud-eyebrow block mb-2">Axe de variation</label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
              {(options || []).map((cat) => {
                const Icon = CATEGORY_ICONS[cat.id] || Sparkles;
                const active = category === cat.id;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setCategory(cat.id)}
                    data-testid={`variation-category-${cat.id}`}
                    className="inline-flex flex-col items-center justify-center gap-1 rounded-lg py-2.5 text-[11px] font-medium transition-colors"
                    style={{
                      background: active ? 'rgba(217,160,91,0.16)' : 'var(--c-surface-2)',
                      color:      active ? 'var(--c-gold)'         : 'var(--c-text-2)',
                      border: `1px solid ${active ? 'var(--c-border-accent)' : 'var(--c-border)'}`,
                    }}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.6} />
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Value picker (varies per category) */}
          {currentCat && (
            <div className="mb-5">
              <label className="hud-eyebrow block mb-2">
                {currentCat.id === 'material' ? 'Zone à modifier' : 'Préréglage'}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {(currentCat.options || []).map((o) => {
                  const active = value === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setValue(o.value)}
                      data-testid={`variation-value-${o.value}`}
                      className="rounded-full px-3 py-1.5 text-[11.5px] font-medium transition-colors"
                      style={{
                        background: active ? 'rgba(217,160,91,0.22)' : 'transparent',
                        color:      active ? 'var(--c-gold)' : 'var(--c-text-2)',
                        border: `1px solid ${active ? 'var(--c-border-accent)' : 'var(--c-border)'}`,
                      }}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Free text — only for material (required) */}
          {needsNote && (
            <div className="mb-5">
              <label className="hud-eyebrow block mb-2">
                Nouveau matériau <span style={{ color: 'var(--c-gold)' }}>· requis</span>
              </label>
              <textarea
                value={customNote}
                onChange={(e) => setCustomNote(e.target.value)}
                rows={3}
                data-testid="variation-custom-note"
                placeholder="Ex: parquet chêne fumé verni mat, lames 20 cm…"
                className="w-full rounded-lg px-3 py-2 text-[13px]"
                style={{
                  background: 'var(--c-surface-2)',
                  color: 'var(--c-text)',
                  border: '1px solid var(--c-border)',
                  resize: 'vertical',
                }}
              />
              <p className="text-[11px] mt-1" style={{ color: 'var(--c-text-muted)' }}>
                Décris précisément le matériau de remplacement. Tout le reste sera conservé à l'identique.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2 p-4"
          style={{ borderTop: '1px solid var(--c-border)', background: 'var(--c-surface-1)' }}
        >
          <HudButton
            variant="ghost"
            size="sm"
            onClick={onClose}
            data-testid="variation-cancel"
          >
            Annuler
          </HudButton>
          <HudButton
            variant="primary"
            size="sm"
            disabled={!canSubmit}
            onClick={onSubmit}
            leftIcon={submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            data-testid="variation-submit"
          >
            {submitting ? 'Lancement…' : 'Lancer la variation'}
          </HudButton>
        </div>
      </div>
    </div>
  );
};

export default VariationDialog;
